import { CreateOrderRequest, ExperienceOption } from '@/types'
import React, { useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import { getExperienceOptionPriceRange } from '@/helper/utils'
import { formatPrice } from '@/helper/formatter'

const ExperienceOptionFromPrice = ({
  option,
  values,
  setFieldValue,
  hidePrice = false,
}: {
  option: ExperienceOption
  values: CreateOrderRequest
  setFieldValue: (field: string, value: any) => void
  /**
   * Suppresses the price label on the option row. Used in voucher
   * redemption mode where showing "Gratuito" / "Da X€" is confusing —
   * the guest already paid via the bundle.
   */
  hidePrice?: boolean
}) => {
  const { t } = useTranslation('experience')

  const fromPrice = useMemo(() => {
    const { isAllFree, allHideFreeLabel, min } = getExperienceOptionPriceRange(option)

    if (isAllFree) {
      return allHideFreeLabel ? null : t('common:free')
    }

    return t('common:from_price', {
      price: formatPrice(min),
    })
  }, [option])

  const ageRestriction = useMemo(() => {
    if (!option.minAge && !option.maxAge) return null

    if (option.minAge && option.maxAge) {
      return `${option.minAge}-${option.maxAge} ${t('years')}`
    }

    if (option.minAge) {
      return `${option.minAge}+ ${t('years')}`
    }

    return `0-${option.maxAge} ${t('years')}`
  }, [option.minAge, option.maxAge, t])

  const isDisabled = !values.selectedDate

  return (
    <div
      key={option.id}
      aria-disabled={isDisabled}
      className={`border rounded-lg py-1.5 px-4 transition-all ${
        isDisabled
          ? 'border-border opacity-50 cursor-not-allowed pointer-events-none'
          : 'cursor-pointer'
      } ${
        values.selectedOption === String(option.id)
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50'
      }`}
      onClick={() => {
        if (isDisabled) return

        setFieldValue('selectedPrices', {})
        setFieldValue('selectedOption', String(option.id))
      }}
    >
      <div className="flex justify-between items-center">
        <div className="flex flex-col">
          <span className="font-medium">{option.name}</span>
          {ageRestriction && (
            <span className="text-xs text-muted-foreground">{ageRestriction}</span>
          )}
        </div>
        {!hidePrice && <span className="text-sm">{fromPrice}</span>}
      </div>
    </div>
  )
}

export default ExperienceOptionFromPrice
